import React, { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { AppSpin } from '@/components/atoms';
import PatientPageHeader from '../components/PatientPageHeader';
import PatientCheckinMailForm from '../components/PatientCheckinMailForm';
import PatientCheckinMailFormBottomBar from '../components/PatientCheckinMailFormBottomBar';
import { useMainPatient } from '@/hooks/main/useMainPatient';
import { useCheckinMailCreate } from '@/hooks/main/useCheckinMailEdit';
import useAppToast from '@/hooks/useAppToast';
import { authorizeNavigation } from '@/hooks/useFormConfirmationBlocker';
import { getApiErrorMessage } from '@/utils/apiError';
import { TENANT_ROUTES } from '@/constants/routes';
import type { CheckinMailFormSchema } from '@/schemas/checkinMailSchema';
import type { CheckinMailPayload } from '@/services/main/checkInMain/checkinMailService';
import { NotFound } from '@/pages/main/system/NotFound';

const PatientCheckinMailCreatePage: React.FC = () => {
  const { id, tenant } = useParams<{ tenant?: string; id?: string }>();
  const navigate = useNavigate();
  const toast = useAppToast();
  const patientId = id ? parseInt(id, 10) : null;
  const patientIdStr = id ?? '';

  const { data: patient, isLoading: patientLoading } = useMainPatient(patientId ?? undefined);
  const createMutation = useCheckinMailCreate(patientIdStr);

  const listUrl = TENANT_ROUTES.CHECKIN_MAILS.LIST(tenant ?? '', patientIdStr);

  const handleBack = useCallback(() => {
    navigate(listUrl);
  }, [navigate, listUrl]);

  const handleSubmit = useCallback(
    async (values: CheckinMailFormSchema) => {
      const payload: CheckinMailPayload = {
        email: values.email,
        is_status: !!values.is_status,
        add_sentence: values.add_sentence ?? '',
        remarks: values.remarks ?? '',
      };
      try {
        await createMutation.mutateAsync(payload);
        toast.success('更新が正常に終了しました。');
        authorizeNavigation();
        navigate(listUrl);
      } catch (err) {
        toast.error(getApiErrorMessage(err, '更新ができませんでした。'));
      }
    },
    [createMutation, toast, navigate, listUrl]
  );

  if (patientId === null || isNaN(patientId)) {
    return <NotFound />;
  }

  if (patientLoading && !patient) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <AppSpin size="large" />
      </div>
    );
  }

  if (!patient) {
    return <NotFound />;
  }

  const formId = 'patient-checkin-mail-create-form';
  const patientName = patient?.current_patient_information?.name ?? '';
  const headerLabel = patient?.serial_number
    ? `${patient.serial_number}：${patientName || '—'}`
    : patientIdStr;

  return (
    <div className="patient-edit-page pb-24">
      <PatientPageHeader
        label={headerLabel}
        patientId={patientIdStr}
        tenant={tenant}
        navigate={path => navigate(path)}
        activeKey="checkin-mails"
        disabled={createMutation.isPending}
      />


      <div>
        <PatientCheckinMailForm
          formId={formId}
          mode="create"
          patientName={patientName}
          disabled={createMutation.isPending}
          onSubmit={handleSubmit}
        />
      </div>

      <PatientCheckinMailFormBottomBar
        mode="create"
        isSubmitting={createMutation.isPending}
        formId={formId}
        onBack={handleBack}
      />
    </div>
  );
};

export default PatientCheckinMailCreatePage;
